import { ActionTree, MutationTree, GetterTree } from 'vuex';

import {
  TWISM_COUNTRIES_LOCAL_STORAGE_KEY,
  EUnavailableStatus,
  IInternationalization,
  IInternationalizationFeaturesStatuses,
  TInternalizationIntegrations
} from '@/models/internationalization';
import InternationalizationService from '@/api/InternationalizationService';
import { IRouterProps } from '@/router/interfaces/router-props.interface';

import Module from '../interfaces/module.interface';
import { RootState } from '../types';
import {
  getOauthState,
  isExternalOauthSession,
  isOauthSession
} from '@/router/router-utils';
import { mapOauthStateToStoreDetailsForm } from './businessOnboarding';

const internationalizationService = new InternationalizationService();

interface IInternationalizationState {
  country: IInternationalization;
  features: IInternationalizationFeaturesStatuses;
  integrations: TInternalizationIntegrations;
  countries: IInternationalization[];
  currentCountry: Record<string, any> | null;
}

export const state: IInternationalizationState = {
  country: {} as IInternationalization,
  features: {
    isInitialized: false,
    loyalty: null
  },
  integrations: [],
  countries: [],
  currentCountry: null
};

const factory = async (context: IRouterProps) => {
  const load = async () => {
    const previousCountries = sessionStorage.getItem(
      TWISM_COUNTRIES_LOCAL_STORAGE_KEY
    );

    let countries: IInternationalization[] = {} as IInternationalization[];

    if (previousCountries) {
      countries = JSON.parse(previousCountries) as IInternationalization[];
    } else {
      const comingSoonCountries: string[] = [];

      const {
        data: response
      } = await internationalizationService.getCountries();

      const allCountriesFormatted = response.data.map((country: any) => {
        const { availability } = country.metadata
          ? country.metadata
          : { availability: { physical: 'Disabled', online: 'Disabled' } };
        const availableOnline = availability.online == 'Enabled';
        const availablePhysical = availability.physical == 'Enabled';

        const countryFormatted: IInternationalization = {
          countryIso2: country.iso2,
          langCode: country.lang,
          name: country.name,
          phoneMask: country.phoneMask,
          phoneCode: country.phoneCode,
          currency: country.currency,
          currencySymbol: country.currencySymbol,
          currencyName: country.label,
          currencyShorthand: country.currency,
          enabled: country.enabled
        };

        if (comingSoonCountries.includes(country.name)) {
          return {
            ...countryFormatted,
            status: EUnavailableStatus.COMING_SOON
          };
        }

        if (availableOnline && availablePhysical) {
          return {
            ...countryFormatted,
            currencySign: `${country.currency} ${country.currencySymbol}`,
            status: 'full'
          };
        }
        if (availableOnline) {
          return {
            ...countryFormatted,
            currencySign: `${country.currency} ${country.currencySymbol}`,
            status: 'online_only'
          };
        }

        return {
          ...countryFormatted,
          status: EUnavailableStatus.UNDATED
        };
      });

      countries = allCountriesFormatted.sort((a: any, b: any) => {
        const valueA = a.name.toUpperCase();
        const valueB = b.name.toUpperCase();

        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      });

      sessionStorage.setItem(
        TWISM_COUNTRIES_LOCAL_STORAGE_KEY,
        JSON.stringify(countries)
      );
    }

    context.store.dispatch('internationalization/SET_COUNTRIES', countries);

    const data = context.store.getters['business/getBusinessState'];

    if (data) {
      const country = countries.find(
        country => country.countryIso2 === data.countryIso2
      );

      if (country) {
        context.store.dispatch(
          'internationalization/SET_COUNTRY_SETTINGS',
          country
        );
      }

      if (
        context.store.getters['internationalization/getIntegrations'].length ===
        0
      )
        internationalizationService
          .getCountryIntegrations(data.country)
          .then(response => {
            context.store.dispatch(
              'internationalization/SET_INTEGRATIONS',
              response.data.items
            );
          });

      // app.$i18n.locale = String(
      //   InternationalizationDictionary[data.country as ECountries].langCode
      // );
    } else if (isOauthSession() && isExternalOauthSession()) {
      const { address }: any = mapOauthStateToStoreDetailsForm(getOauthState());
      if (address) {
        const country = countries.find(
          country => country.countryIso2 === address.countryCode
        );

        if (country) {
          context.store.dispatch(
            'internationalization/SET_COUNTRY_SETTINGS',
            country
          );
        }
      }
    }
  };

  await load();

  context.store.watch(
    (s, getters) => getters['business/getBusinessState'],
    load
  );
};

const actions: ActionTree<IInternationalizationState, RootState> = {
  SET_COUNTRY_SETTINGS(context, payload: IInternationalization) {
    context.commit('setCountrySettings', payload);
  },
  SET_INTEGRATIONS(context, payload: TInternalizationIntegrations) {
    context.commit('setIntegrations', payload);
  },
  SET_COUNTRIES(context, payload: IInternationalization[]) {
    context.commit('setCountries', payload);
  },
  SET_CURRENT_COUNTRY(context, payload: Record<string, any>) {
    context.commit('setCurrentCountry', payload);
  }
};

const mutations: MutationTree<IInternationalizationState> = {
  setCountrySettings(state, payload: IInternationalization) {
    state.country = payload;
  },
  setIntegrations(state, payload: TInternalizationIntegrations) {
    state.integrations = payload;
  },
  setCountries(state, payload: IInternationalization[]) {
    state.countries = payload;
  },
  setCurrentCountry(state, payload: Record<string, any>) {
    state.currentCountry = payload;
  }
};

const getters: GetterTree<IInternationalizationState, RootState> = {
  getCountrySettings(state) {
    return state.country;
  },
  getCountryName(state) {
    return state.country ? state.country.name : '';
  },
  getIntegrations(state) {
    return state.integrations;
  },
  getCountries(state) {
    return state.countries;
  },
  getCurrentCountry(state) {
    return state.currentCountry;
  }
};

const namespaced: boolean = true;

export const internationalization: Module<
  IInternationalizationState,
  RootState
> = {
  namespaced,
  initializer: {
    factory,
    provide: 'InternationalizationStore',
    requires: ['BusinessStore'],
    on: 'BeforeEachRoute'
  },
  state,
  actions,
  mutations,
  getters
};
